import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import ClearIcon from "@mui/icons-material/Clear";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import CustomerModel from "./popups/customerlist/CustomerModel";
import RecentBillModel from "./recentbills/RecentBillModel";
import SalesReturnSearchModal from "./popups/salesreturn/SalesReturnSearchModal";
import { getDecryptedToken } from "../../../utils/crypto/cryptoUtils";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import subscriptionService, {
  SubscriptionData,
} from "../../../services/subscription/subscriptionService";
import TvIcon from "@mui/icons-material/Tv";
import PaymentIcon from "@mui/icons-material/Payment";
import PosModel from "./popups/posmodel/PosModel";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store/store";
import {
  clearCustomer,
  setSaleType,
  setSaleDate,
  clearSaleDate,
} from "../../../store/pos/posSlice";
import InstantItem from "./InstantItem";
import { openCustomerView } from "../utils/openCustomerView";
import { detectExternalScreen } from "../utils/useScreenManager";
import { toast } from "react-toastify";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReplayIcon from "@mui/icons-material/Replay";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

// Map currency symbols to time zones
const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York", // US Dollar
  "¥": "Asia/Tokyo", // Japanese Yen (default to Tokyo)
  "Rs.": "Asia/Colombo", // Sri Lankan Rupee
  "€": "Europe/Berlin", // Euro
  "£": "Europe/London", // British Pound
  "₹": "Asia/Kolkata", // Indian Rupee
  A$: "Australia/Sydney", // Australian Dollar
  C$: "America/Toronto", // Canadian Dollar
  // Add more as needed
};

// const now = new Date();
const shopName = getDecryptedToken("martName");
const name = getDecryptedToken("name");
const currency = getDecryptedToken("currency");
const designation = (getDecryptedToken("designation") || "").toLowerCase();

// Only admin or manager roles can create backdated sales
const canBackdate =
  designation.includes("admin") || designation.includes("manager");

function PosTop({
  onEditRecentBill,
}: {
  onEditRecentBill?: (billNumber: string | null) => void;
}) {
  const [isMOdelOpen, setModelOpen] = useState<boolean>(false);
  const [isPosMOdelOpen, setPosModelOpen] = useState<boolean>(false);
  const [isRecentBillsOpen, setRecentBillsOpen] = useState<boolean>(false);
  const [isSalesReturnOpen, setSalesReturnOpen] = useState<boolean>(false);
  const [isAddItemOpen, setAddItemOpen] = useState<boolean>(false);
  const [subscriptionData, setSubscriptionData] =
    useState<SubscriptionData | null>(null);
  const [subscriptionTimer, setSubscriptionTimer] = useState<string>("");

  // Backdated sale state
  const [isBackdatePickerOpen, setBackdatePickerOpen] =
    useState<boolean>(false);
  const [pickerValue, setPickerValue] = useState<Dayjs | null>(null);

  const dispatch = useDispatch();
  const { customerId, customerName, customerPhone, customerType, saleType } =
    useSelector((state: RootState) => state.pos.current);
  const saleDate = useSelector((state: RootState) => state.pos.saleDate);
  const navigate = useNavigate();

  const openModel = () => {
    setModelOpen(true);
  };
  const openPosModel = () => {
    setPosModelOpen(true);
  };
  const openRecentBills = () => {
    setRecentBillsOpen(true);
  };
  const openSalesReturn = () => {
    setSalesReturnOpen(true);
  };
  const openAddItem = () => {
    setAddItemOpen(true);
  };

  const isClose = () => {
    setModelOpen(false);
    setPosModelOpen(false);
    setRecentBillsOpen(false);
    setSalesReturnOpen(false);
    setAddItemOpen(false);
  };

  const handleClearCustomer = () => {
    dispatch(clearCustomer());
  };

  const handleSaleTypeToggle = (type: "cash" | "credit") => {
    dispatch(setSaleType(type));
  };

  const handleOpenCustomerView = async () => {
    try {
      // Try to detect external screen first
      const externalScreen = await detectExternalScreen();

      if (externalScreen) {
        // Open on external screen if available
        openCustomerView(externalScreen);
      } else {
        // Fallback: open on current screen with fullscreen
        // Create a default screen object for current window
        const defaultScreen = {
          left: 0,
          top: 0,
          width: window.screen.width,
          height: window.screen.height,
          isPrimary: true,
        };
        openCustomerView(defaultScreen);
      }
    } catch (error) {
      console.error("Failed to open customer view:", error);
      toast.error(
        "Failed to open customer view. Please check browser permissions.",
      );
    }
  };

  // Backdated sale handlers
  const handleOpenBackdatePicker = () => {
    setPickerValue(dayjs().subtract(1, "day")); // default to yesterday
    setBackdatePickerOpen(true);
  };

  const handleConfirmBackdate = () => {
    if (!pickerValue) return;
    const selected = pickerValue.toDate();
    if (selected >= new Date()) {
      toast.error("Please select a past date and time");
      return;
    }
    dispatch(setSaleDate(selected.toISOString()));
    setBackdatePickerOpen(false);
  };

  const handleCancelBackdate = () => {
    setBackdatePickerOpen(false);
  };

  const handleClearBackdate = () => {
    dispatch(clearSaleDate());
  };

  // Format backdate for display
  const formatBackdate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Live time logic (date and time only)
  const [currentTime, setCurrentTime] = useState(new Date());

  // Pick time zone based on currency symbol, fallback to browser time zone
  const timeZone =
    currencyTimeZoneMap[currency as string] ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch subscription data on mount
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        // Use getCurrentBranchSubscription since POS uses branch token
        const data = await subscriptionService.getCurrentSubscription();
        setSubscriptionData(data);
        console.log("Subscription Data:", data);
      } catch (error) {
        console.error("Error fetching subscription:", error);
      }
    };
    fetchSubscription();
  }, []);

  // Update subscription timer every second
  useEffect(() => {
    if (subscriptionData?.subscription) {
      const updateTimer = () => {
        const endDate = new Date(subscriptionData.subscription!.endDate);
        const timeRemaining =
          subscriptionService.calculateTimeRemaining(endDate);
        setSubscriptionTimer(
          subscriptionService.formatTimeRemaining(timeRemaining),
        );
      };

      updateTimer();
      const timer = setInterval(updateTimer, 1000);
      return () => clearInterval(timer);
    }
  }, [subscriptionData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <>
        <div className="pos_top_container">
          <div className="left">
            <div className="data_now">
              <AccountCircleIcon />
              {name}
              <span style={{ fontSize: "0.9em", opacity: 0.9, marginLeft: 6 }}>
                {currentTime.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "2-digit",
                  timeZone,
                })}{" "}
                {currentTime.toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: true,
                  timeZone,
                })}
              </span>
            </div>
            <div className="top-divider" />
            <div className="data_now">{shopName}</div>

            {/* Backdated Sale Banner */}
            {saleDate && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#c9a24d",
                  color: "#fff",
                  borderRadius: 6,
                  padding: "4px 10px",
                  fontWeight: 600,
                  fontSize: "0.85em",
                  marginTop: 4,
                }}
              >
                <HistoryIcon style={{ fontSize: 16 }} />
                BACKDATED: {formatBackdate(saleDate)}
                <button
                  onClick={handleClearBackdate}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    padding: "0 2px",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Clear backdated mode"
                >
                  <ClearIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            )}

            {/* Customer Display Section */}
            {customerId && (
              <div className="customer_display">
                <div className="selected_customer">
                  <PersonIcon className="customer_icon" />
                  <div className="customer_info">
                    <span className="customer_name">{customerName}</span>
                    {customerPhone && (
                      <span className="customer_phone">{customerPhone}</span>
                    )}
                    <span className={`customer_type ${customerType}`}>
                      {customerType?.toUpperCase()}
                    </span>
                  </div>

                  {/* Sale Type Toggle - Only show when customer is selected */}
                  <div className="sale_type_toggle">
                    <div className="toggle_buttons">
                      <button
                        className={`toggle_btn ${
                          saleType === "cash" ? "active" : ""
                        }`}
                        onClick={() => handleSaleTypeToggle("cash")}
                      >
                        Cash Sale
                      </button>
                      <button
                        className={`toggle_btn credit ${
                          saleType === "credit" ? "active" : ""
                        }`}
                        onClick={() => handleSaleTypeToggle("credit")}
                      >
                        Credit Sale
                      </button>
                    </div>
                  </div>

                  <button
                    className="clear_customer_btn"
                    onClick={handleClearCustomer}
                    title="Clear Customer"
                  >
                    <ClearIcon />
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="right">
            <button onClick={() => navigate("/")}>
              <AccountBalanceIcon /> Dashboard
            </button>
            <button onClick={openPosModel}>
              <AttachMoneyIcon /> Cash
            </button>
            <button onClick={openModel}>
              <AddIcon /> Customer
            </button>
            <button onClick={openAddItem}>
              <InventoryIcon /> Item
            </button>
            <button onClick={openRecentBills}>
              <ReceiptLongIcon /> Recent Bills
            </button>
            <button onClick={openSalesReturn}>
              <ReplayIcon /> Sales Return
            </button>
            <button onClick={handleOpenCustomerView}>
              <TvIcon /> View
            </button>
            {canBackdate && (
              <button
                onClick={handleOpenBackdatePicker}
                style={{
                  background: saleDate ? "#c9a24d" : undefined,
                  borderColor: saleDate ? "#c9a24d" : undefined,
                  color: saleDate ? "#fff" : undefined,
                }}
                title="Enter a sale for a past date"
              >
                <HistoryIcon /> BACKDATED
              </button>
            )}
            <div className="subscription_payment_container">
              {subscriptionTimer && (
                <div className="subscription_timer_display">
                  <AccessTimeIcon
                    className="timer_icon"
                    style={{
                      color:
                        subscriptionData?.timeRemaining?.days &&
                        subscriptionData?.timeRemaining?.days <= 3
                          ? "#ff4444"
                          : "#1f6f8b",
                    }}
                  />
                  <span className="timer_text">{subscriptionTimer}</span>
                </div>
              )}
              <button
                className="pay_button"
                onClick={() =>
                  navigate("/shop/pay", { state: { subscriptionData } })
                }
              >
                <PaymentIcon /> Pay
              </button>
            </div>
          </div>
        </div>

        {/* Backdated Sale Date/Time Picker Modal */}
        {isBackdatePickerOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0,0,0,0.5)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                padding: 28,
                minWidth: 320,
                display: "flex",
                flexDirection: "column",
                gap: 16,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#333",
                }}
              >
                <HistoryIcon style={{ color: "#c9a24d" }} />
                Select Backdated Sale Date & Time
              </h3>
              <p style={{ margin: 0, fontSize: "0.85em", color: "#666" }}>
                This sale will be recorded under the selected past date in all
                reports and will be marked as "Backdated".
              </p>
              <DateTimePicker
                label="Sale Date & Time"
                value={pickerValue}
                onChange={(val) => setPickerValue(val)}
                disableFuture
                slotProps={{
                  textField: { fullWidth: true, size: "small" },
                }}
              />
              <div
                style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
              >
                <button
                  onClick={handleCancelBackdate}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 6,
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmBackdate}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 6,
                    border: "none",
                    background: "#c9a24d",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {isMOdelOpen && (
          <CustomerModel isCloseMain={isClose} isOpen={isMOdelOpen} />
        )}
        {isPosMOdelOpen && (
          <PosModel isCloseMain={isClose} isOpen={isPosMOdelOpen} />
        )}
        {isRecentBillsOpen && (
          <RecentBillModel
            isCloseMain={isClose}
            isOpen={isRecentBillsOpen}
            onEditBill={onEditRecentBill}
          />
        )}
        {isSalesReturnOpen && (
          <SalesReturnSearchModal
            isOpen={isSalesReturnOpen}
            onClose={() => setSalesReturnOpen(false)}
          />
        )}
        {isAddItemOpen && (
          <InstantItem setopen={setAddItemOpen} open={isAddItemOpen} />
        )}
      </>
    </LocalizationProvider>
  );
}

export default PosTop;
